import {
  CharacterGetRes,
  CharacterGetResWithStory,
  StoryGetRes,
} from '@/types/character'
import { isVideoUrlShared } from '@/constant/prefetch-home-image'
import type { HomeFirstScreenMediaUnit } from '@/utils/homeFirstScreenRumMetrics'

export type HomeFeedMediaItem =
  | CharacterGetRes
  | CharacterGetResWithStory
  | StoryGetRes

type BuildHomeMediaUnitsOptions = {
  bannerInterval: number
  hasBanner: boolean
}

export const getHomeBannerMediaKey = (index: number | 'fallback') =>
  `banner:${index}`

export const getHomeFeedItemMediaKey = (
  item: HomeFeedMediaItem,
  index: number
) => {
  const maybeForYouItem = item as CharacterGetResWithStory

  if (maybeForYouItem.type === 'story') {
    const story = maybeForYouItem.data as StoryGetRes
    return `story:${story.story_id || index}`
  }

  if (maybeForYouItem.type === 'character') {
    const character = (maybeForYouItem.data as CharacterGetRes).character
    const prefix = character?.character_type === 9 ? 'game' : 'character'
    return `${prefix}:${character?.character_id || index}`
  }

  if ('story_id' in item && item.story_id) {
    return `story:${item.story_id}`
  }

  const character = (item as CharacterGetRes).character
  const prefix = character?.character_type === 9 ? 'game' : 'character'
  return `${prefix}:${character?.character_id || index}`
}

const getHomeFeedItemMediaType = (
  item: HomeFeedMediaItem
): HomeFirstScreenMediaUnit['mediaType'] => {
  const maybeForYouItem = item as CharacterGetResWithStory
  let animatedImageUrl = ''

  if (maybeForYouItem.type === 'story') {
    animatedImageUrl =
      (maybeForYouItem.data as StoryGetRes).animated_image_url || ''
  } else if (maybeForYouItem.type === 'character') {
    animatedImageUrl =
      (maybeForYouItem.data as CharacterGetRes).character?.animated_image_url ||
      ''
  } else if ('story_id' in item) {
    animatedImageUrl = (item as StoryGetRes).animated_image_url || ''
  } else {
    animatedImageUrl =
      (item as CharacterGetRes).character?.animated_image_url || ''
  }

  return isVideoUrlShared(animatedImageUrl) ? 'video' : 'image'
}

export const getHomeFirstScreenMediaUnits = (
  items: HomeFeedMediaItem[],
  { bannerInterval, hasBanner }: BuildHomeMediaUnitsOptions
): HomeFirstScreenMediaUnit[] => {
  const units: HomeFirstScreenMediaUnit[] = []
  let bannerInserted = false

  items.forEach((item, index) => {
    const shouldRenderBanner =
      hasBanner && index >= 2 && (index - 2) % bannerInterval === 0

    if (shouldRenderBanner) {
      bannerInserted = true
      units.push({
        key: getHomeBannerMediaKey(index),
        type: 'banner',
        mediaType: 'banner',
      })
    }

    units.push({
      key: getHomeFeedItemMediaKey(item, index),
      type: 'card',
      mediaType: getHomeFeedItemMediaType(item),
    })
  })

  if (!bannerInserted && hasBanner && items.length > 0) {
    units.push({
      key: getHomeBannerMediaKey('fallback'),
      type: 'banner',
      mediaType: 'banner',
    })
  }

  return units
}
